import React, { useState, useRef, useEffect, useLayoutEffect, useMemo } from "react";
import * as DateUtils from "../utils/dateUtils.js";
import { getAllProjects } from "../services/azureProjectsService";
import { getHrSettings } from "../services/azureHrService";

// Module-level variables to persist across component remounts
let globalIsSelecting = false;
let globalSelectionStart = null;
let globalScrollPosition = null; // Store scroll position across remounts
let globalIsShiftDrag = false; // Track if shift was held when drag started
let globalPreviousSelection = []; // Store previous selection for shift-drag

// Verschieben/Groesse aendern eines bestehenden Zeitblocks per Maus (Klick auf
// Zellkoerper = verschieben, Klick auf Resize-Handle = Anfang/Ende ziehen).
// Ob eine Interaktion ein reiner Klick (oeffnet die Bearbeitung direkt im
// mouseup-Handler) oder ein tatsaechlicher Drag war, entscheidet sich beim
// mouseup anhand von globalDragMoved und der Mausdistanz seit mousedown.
let globalDragMode = null; // null | "move" | "resize-start" | "resize-end"
let globalDragEntry = null;
let globalDragDay = null; // ISO-Datum, auf das der Drag beschraenkt ist (kein Tageswechsel)
let globalDragOffsetSlots = 0; // fuer "move": Abstand zwischen gegriffener Zelle und Eintrags-Start
let globalDragSlotCount = 0; // fuer "move": Dauer des Eintrags in Slots (bleibt beim Verschieben gleich)
let globalDragFixedSlot = 0; // fuer "resize-*": Slot des NICHT gezogenen Endes
let globalDragMoved = false;
let globalDragPreview = null; // { day, startSlot, endSlot } aktueller Vorschau-Bereich
let globalDragStartPos = null; // { x, y } Mausposition beim mousedown (Klick-Erkennung)
const CLICK_MAX_DISTANCE_PX = 5;

const Calendar = ({
  currentWeek,
  setCurrentWeek,
  selectedCells,
  setSelectedCells,
  onCreateEntry,
  onDeleteEntry,
  onEditEntry,
  onAdjustEntry,
  recentProjects = [],
  onQuickCreateEntry,
  timeEntries,
  contracts,
  themeValues,
  loading = false, // Neuer Loading Prop
}) => {
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  // Live-Vorschau waehrend Verschieben/Resize: { entry, day, startSlot, endSlot }.
  // Treibt getTimeEntryForCell/getCellClassName, damit der Block optisch zur
  // Mausposition "wandert", statt erst nach dem Loslassen zu springen.
  const [dragPreviewState, setDragPreviewState] = useState(null);
  // Rechtsklick-Kontextmenue: { x, y, day, hour, isHalf }
  const [contextMenu, setContextMenu] = useState(null);
  const [projects, setProjects] = useState([]);
  const [hrSettings, setHrSettings] = useState(null);
  const calendarRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const isSelectingRef = useRef(false); // Ref to track selection state across re-renders
  const selectionStartRef = useRef(null); // Ref to track selection start across re-renders

  const weekDays = DateUtils.getWeekDays(currentWeek);
  const hours = Array.from({ length: 19 }, (_, i) => i + 5); // 5:00 bis 23:30 (19 Stunden)

  // Load projects for color lookup
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const projectsData = await getAllProjects();
        setProjects(projectsData);
      } catch (error) {
        console.error("Error loading projects for colors:", error);
      }
    };
    loadProjects();
  }, []);

  // Load HR settings for Feiertage/Betriebsferien (yearly)
  useEffect(() => {
    const year = String(currentWeek.getFullYear());
    getHrSettings(year)
      .then(setHrSettings)
      .catch(() => setHrSettings(null));
  }, [currentWeek]);

  const feiertageSet = useMemo(
    () =>
      new Set(
        (hrSettings?.feiertage || []).map((f) =>
          typeof f === "string" ? f : f.date
        )
      ),
    [hrSettings]
  );
  const betriebsferienSet = useMemo(
    () => new Set(hrSettings?.betriebsferien || []),
    [hrSettings]
  );

  // Liefert "weekend" | "feiertag" | "betriebsferien" | null
  const getDayKind = (day) => {
    const dateStr = DateUtils.formatDate(day);
    const dow = day.getDay();
    if (dow === 0 || dow === 6) return "weekend";
    if (feiertageSet.has(dateStr)) return "feiertag";
    if (betriebsferienSet.has(dateStr)) return "betriebsferien";
    return null;
  };

  const getFeiertagName = (day) => {
    const dateStr = DateUtils.formatDate(day);
    const match = (hrSettings?.feiertage || []).find(
      (f) => (typeof f === "string" ? f : f.date) === dateStr
    );
    return match && typeof match === "object" ? match.name : null;
  };

  // Alle Ganztag-Einträge für diesen Tag (für die Pill im day-header)
  const getFullDayEntriesForDay = (day) => {
    const cellDate = DateUtils.formatDate(day);
    return timeEntries.filter(
      (entry) => entry.isFullDay && entry.date === cellDate
    );
  };

  // Auto-scroll zu 07:00 bei Laden/Wechsel der Woche
  useEffect(() => {
    if (scrollContainerRef.current) {
      // 07:00 ist Index 2 (5, 6, 7...)
      // Jede Stunde hat 2 Slots (voll + halb) = 4rem total height
      const targetHour = 7;
      const hourIndex = targetHour - 5; // Index von 07:00
      const scrollPosition = hourIndex * 4 * 16; // 4rem * 16px = 64px pro Stunde
    }
  }, [currentWeek]);

  useEffect(() => {
    document.body.classList.add("wide-page");
    return () => {
      document.body.classList.remove("wide-page");
    };
  }, []);

  // Navigation
  const navigateWeek = (direction) => {
    const newWeek = new Date(currentWeek);
    newWeek.setDate(newWeek.getDate() + direction * 7);
    setCurrentWeek(newWeek);
    setSelectedCells([]);
  };

  // Cell Selection Logic
  const getCellKey = (day, hour, isHalf) => {
    return `${DateUtils.formatDate(day)}-${hour}-${isHalf}`;
  };

  const isCellSelected = (day, hour, isHalf) => {
    const key = getCellKey(day, hour, isHalf);
    return selectedCells.some((cell) => cell.key === key);
  };

  const getTimeEntryForCell = (day, hour, isHalf) => {
    const cellTime = DateUtils.formatTime(hour, isHalf);
    const cellDate = DateUtils.formatDate(day);

    // Waehrend eines Verschieben/Resize-Drags "wandert" der Zeitblock optisch
    // zur aktuellen Vorschauposition: seine urspruenglichen Zellen ausserhalb
    // der Vorschau geben als frei, die Vorschau-Zellen liefern eine virtuelle
    // Kopie des Eintrags mit den Vorschau-Zeiten (damit Erste/Letzte-Zelle,
    // Farbe und Inhalt gegen die NEUE Position statt die alte pruefen).
    if (dragPreviewState) {
      const { entry, day: previewDay, startSlot, endSlot } = dragPreviewState;
      const slot = DateUtils.getSlotFromTime(hour, isHalf);
      const inPreview =
        cellDate === previewDay && slot >= startSlot && slot < endSlot;
      const isOriginalCell =
        entry.date === cellDate &&
        cellTime >= entry.startTime &&
        cellTime < entry.endTime;

      if (inPreview) {
        const { hour: sh, half: shalf } = DateUtils.getTimeFromSlot(startSlot);
        const { hour: eh, half: ehalf } = DateUtils.getTimeFromSlot(endSlot);
        return {
          ...entry,
          date: previewDay,
          startTime: DateUtils.formatTime(sh, shalf),
          endTime: DateUtils.formatTime(eh, ehalf),
          __isDragPreview: true,
        };
      }
      if (isOriginalCell) {
        return undefined; // urspruengliche Stelle: waehrend des Drags "frei"
      }
    }

    return timeEntries.find((entry) => {
      if (entry.date !== cellDate) return false;
      // Ganztag-Eintraege belegen keine Zellen — sie werden als Pill im
      // day-header angezeigt. Zellen bleiben fuer Zeiteintraege frei.
      if (entry.isFullDay) return false;

      const entryStart = entry.startTime;
      const entryEnd = entry.endTime;

      return cellTime >= entryStart && cellTime < entryEnd;
    });
  };

  const isFirstCellOfEntry = (day, hour, isHalf, entry) => {
    const cellTime = DateUtils.formatTime(hour, isHalf);
    return cellTime === entry.startTime;
  };

  const isLastCellOfEntry = (day, hour, isHalf, entry) => {
    const cellTime = DateUtils.formatTime(hour, isHalf);
    // Last cell is the one where next half hour would be >= endTime
    const nextTime = isHalf
      ? DateUtils.formatTime(hour + 1, false)
      : DateUtils.formatTime(hour, true);
    return nextTime >= entry.endTime && cellTime < entry.endTime;
  };

  const getCellClassName = (day, hour, isHalf) => {
    const classes = ["calendar-cell"];
    const timeEntry = getTimeEntryForCell(day, hour, isHalf);

    if (timeEntry) {
      classes.push("occupied");
      if (timeEntry.__isDragPreview) {
        classes.push("drag-preview");
      }
      if (isFirstCellOfEntry(day, hour, isHalf, timeEntry)) {
        classes.push("entry-first");
      }
      if (isLastCellOfEntry(day, hour, isHalf, timeEntry)) {
        classes.push("entry-last");
      }
    } else if (isCellSelected(day, hour, isHalf)) {
      classes.push("selected");
    } else {
      classes.push("available");
    }

    // Non-working days (weekend/feiertag/betriebsferien) bekommen eine
    // dezente graue Hintergrundmarkierung — Zellen bleiben interaktiv,
    // um auch hier Zeiten erfassen zu koennen.
    const dayKind = getDayKind(day);
    const hasAbsence = getFullDayEntriesForDay(day).length > 0;
    if (dayKind || hasAbsence) {
      classes.push("non-working");
      if (dayKind) classes.push(`nw-${dayKind}`);
      if (hasAbsence) classes.push("nw-absence");
    }

    return classes.join(" ");
  };

  // Setzt Vorschau-Bereich + Ziel-Eintrag gleichzeitig als Modul-Variable
  // (fuer die Maus-Handler, remount-sicher) und als State (fuer den Render).
  const setDragPreview = (preview) => {
    globalDragPreview = preview;
    setDragPreviewState(preview);
  };

  // Start eines Resize-Drags über einen der beiden Griffe am Rand eines
  // Zeitblocks (oben = Start ziehen, unten = Ende ziehen). Eigenes
  // onMouseDown statt Delegation an handleMouseDown, damit ein Klick auf den
  // schmalen Griff nicht als Klick auf die Zelle selbst gewertet wird.
  const handleResizeMouseDown = (entry, day, edge, event) => {
    if (event.button !== 0) return; // nur linke Maustaste - Rechtsklick soll das Kontextmenue nicht stoeren
    event.preventDefault();
    event.stopPropagation();
    if (contextMenu) setContextMenu(null);
    if (!onAdjustEntry) return;

    const startSlot = DateUtils.getSlotFromTimeString(entry.startTime);
    const endSlot = DateUtils.getSlotFromTimeString(entry.endTime);

    globalDragMode = edge === "start" ? "resize-start" : "resize-end";
    globalDragEntry = entry;
    globalDragDay = DateUtils.formatDate(day);
    globalDragFixedSlot = edge === "start" ? endSlot : startSlot;
    globalDragMoved = false;
    setSelectedCells([]);
    setDragPreview({ entry, day: globalDragDay, startSlot, endSlot });
  };

  // Restore scroll position after remount (useLayoutEffect prevents flicker)
  useLayoutEffect(() => {
    if (globalScrollPosition !== null && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = globalScrollPosition;
      globalScrollPosition = null; // Clear after restoring
    }
  });

  // Rechtsklick auf eine freie Zelle: eigenes Kontextmenue statt des
  // Browser-Menues, mit "Neuer Eintrag" und Schnellmodus (zuletzt verwendete
  // Projekte, siehe App.jsx `recentProjects`). Auf belegten Zellen (v1)
  // bewusst kein eigenes Menue - dort greift weiterhin Klick/Ziehen.
  const handleCellContextMenu = (day, hour, isHalf, event) => {
    const timeEntry = getTimeEntryForCell(day, hour, isHalf);
    if (timeEntry) return;

    event.preventDefault();
    event.stopPropagation();

    // Eine bereits laufende Mehrfachauswahl bleibt erhalten (Rechtsklick
    // wirkt dann auf die ganze Auswahl); ohne Auswahl wird die angeklickte
    // Zelle als 30-Minuten-Auswahl uebernommen.
    if (selectedCells.length === 0) {
      const dayString = DateUtils.formatDate(day);
      setSelectedCells([
        { date: dayString, hour, half: isHalf, key: getCellKey(day, hour, isHalf) },
      ]);
    }

    setContextMenu({ x: event.clientX, y: event.clientY });
  };

  // Kontextmenue bei Klick ausserhalb, Escape oder Scrollen schliessen.
  // Bewusst Bubble-Phase (nicht Capture): ein Klick auf einen Menuepunkt
  // stoppt dort selbst die Propagation, das Menue bleibt also beim eigenen
  // Klick unangetastet. Klicks auf Kalenderzellen schliessen das Menue
  // stattdessen explizit in handleMouseDown/handleCellContextMenu, da Zellen
  // ihrerseits stopPropagation() aufrufen und diesen Listener nie erreichen.
  useEffect(() => {
    if (!contextMenu) return undefined;
    const close = () => setContextMenu(null);
    const closeOnEscape = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("scroll", close, true);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("scroll", close, true);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [contextMenu]);

  // Mouse Event Handlers - using global variables to persist across remounts
  const handleMouseDown = (day, hour, isHalf, event) => {
    // Nur linke Maustaste: ein Rechtsklick soll ausschliesslich das
    // Kontextmenue (onContextMenu/handleCellContextMenu) auslösen und darf
    // weder eine neue Auswahl starten noch eine bestehende ersetzen.
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    if (contextMenu) setContextMenu(null);

    // Save scroll position before state update causes remount
    if (scrollContainerRef.current) {
      globalScrollPosition = scrollContainerRef.current.scrollTop;
    }

    const timeEntry = getTimeEntryForCell(day, hour, isHalf);
    if (timeEntry) {
      // Ganztag-Eintraege (Pillen im Header) landen nie hier, da sie keine
      // Zellen belegen. Verschieben nur, wenn ueberhaupt etwas zu verschieben
      // gibt (onAdjustEntry vorhanden); sonst wie bisher: Klick oeffnet ueber
      // den normalen onClick-Pfad die Bearbeitung.
      if (!onAdjustEntry) {
        if (onEditEntry) onEditEntry(timeEntry);
        else if (onDeleteEntry) onDeleteEntry(timeEntry);
        return;
      }

      const grabbedSlot = DateUtils.getSlotFromTime(hour, isHalf);
      const startSlot = DateUtils.getSlotFromTimeString(timeEntry.startTime);
      const endSlot = DateUtils.getSlotFromTimeString(timeEntry.endTime);

      globalDragMode = "move";
      globalDragEntry = timeEntry;
      globalDragDay = DateUtils.formatDate(day);
      globalDragOffsetSlots = grabbedSlot - startSlot;
      globalDragSlotCount = endSlot - startSlot;
      globalDragMoved = false;
      globalDragStartPos = { x: event.clientX, y: event.clientY };
      setSelectedCells([]);
      setDragPreview({ entry: timeEntry, day: globalDragDay, startSlot, endSlot });
      // Ob es beim Loslassen ein reiner Klick war (→ Bearbeiten öffnet sich
      // im mouseup-Handler) oder ein Drag, entscheidet sich erst dort. Nicht
      // über den nativen click: das Re-Rendern nach mousedown (Vorschau-State,
      // Auswahl leeren) ersetzt die Zelle, der click kommt dann nicht an.
      return;
    }

    const cellKey = getCellKey(day, hour, isHalf);
    const dayString = DateUtils.formatDate(day);

    const newSelectionStart = {
      day: dayString,
      hour,
      isHalf,
      key: cellKey,
    };

    const newCell = {
      date: dayString,
      hour,
      half: isHalf,
      key: cellKey,
    };

    // Set global variables immediately (persist across remounts)
    globalIsSelecting = true;
    globalSelectionStart = newSelectionStart;
    globalIsShiftDrag = event.shiftKey;

    // Store previous selection if shift is held
    if (event.shiftKey) {
      globalPreviousSelection = [...selectedCells];
    } else {
      globalPreviousSelection = [];
    }

    // Also set state for re-renders
    setIsSelecting(true);
    setSelectionStart(newSelectionStart);

    if (event.shiftKey) {
      // Shift-drag: add new cell to existing selection
      setSelectedCells((prev) => [...prev, newCell]);
    } else {
      // Normal drag: start fresh selection
      setSelectedCells([newCell]);
    }
  };

  // Grenzen des Rasters in Slots (Stunden 5..23, siehe `hours` oben).
  const GRID_MIN_SLOT = DateUtils.getSlotFromTime(5, false); // 05:00
  const GRID_MAX_SLOT_EXCLUSIVE = DateUtils.getSlotFromTime(23, true) + 1; // 24:00

  const handleMoveDragEnter = (day, hour, isHalf) => {
    const dayString = DateUtils.formatDate(day);
    // V1 bewusst nur innerhalb desselben Tages (wie die normale Mehrfachauswahl)
    if (dayString !== globalDragDay) return;

    globalDragMoved = true;
    const hoveredSlot = DateUtils.getSlotFromTime(hour, isHalf);
    let newStart = hoveredSlot - globalDragOffsetSlots;
    newStart = Math.max(
      GRID_MIN_SLOT,
      Math.min(newStart, GRID_MAX_SLOT_EXCLUSIVE - globalDragSlotCount)
    );
    const newEnd = newStart + globalDragSlotCount;
    setDragPreview({ entry: globalDragEntry, day: dayString, startSlot: newStart, endSlot: newEnd });
  };

  const handleResizeDragEnter = (day, hour, isHalf) => {
    const dayString = DateUtils.formatDate(day);
    if (dayString !== globalDragDay) return;

    globalDragMoved = true;
    const hoveredSlot = DateUtils.getSlotFromTime(hour, isHalf);
    let startSlot;
    let endSlot;
    if (globalDragMode === "resize-start") {
      startSlot = Math.max(
        GRID_MIN_SLOT,
        Math.min(hoveredSlot, globalDragFixedSlot - 1)
      );
      endSlot = globalDragFixedSlot;
    } else {
      startSlot = globalDragFixedSlot;
      endSlot = Math.min(
        GRID_MAX_SLOT_EXCLUSIVE,
        Math.max(hoveredSlot + 1, globalDragFixedSlot + 1)
      );
    }
    setDragPreview({ entry: globalDragEntry, day: dayString, startSlot, endSlot });
  };

  const handleMouseEnter = (day, hour, isHalf) => {
    if (globalDragMode === "move") {
      handleMoveDragEnter(day, hour, isHalf);
      return;
    }
    if (globalDragMode === "resize-start" || globalDragMode === "resize-end") {
      handleResizeDragEnter(day, hour, isHalf);
      return;
    }

    // Use global variables (persist across remounts)
    if (!globalIsSelecting || !globalSelectionStart) {
      return;
    }

    // Save scroll position before state update causes remount
    if (scrollContainerRef.current) {
      globalScrollPosition = scrollContainerRef.current.scrollTop;
    }

    const timeEntry = getTimeEntryForCell(day, hour, isHalf);
    if (timeEntry) {
      return;
    }

    // Use global values
    const startDay = globalSelectionStart.day;
    const endDay = DateUtils.formatDate(day);
    const startHour = globalSelectionStart.hour;
    const endHour = hour;
    const startHalf = globalSelectionStart.isHalf;
    const endHalf = isHalf;

    // Only allow selection within the same day
    if (startDay !== endDay) {
      return;
    }

    const newSelection = [];
    const startTime = startHour + (startHalf ? 0.5 : 0);
    const endTime = endHour + (endHalf ? 0.5 : 0);

    const minTime = Math.min(startTime, endTime);
    const maxTime = Math.max(startTime, endTime) + 0.5;

    for (let time = minTime; time < maxTime; time += 0.5) {
      const h = Math.floor(time);
      const half = time % 1 !== 0;
      const key = getCellKey(day, h, half);

      // Skip if cell has time entry
      if (getTimeEntryForCell(day, h, half)) continue;

      newSelection.push({
        date: startDay,
        hour: h,
        half: half,
        key,
      });
    }

    // If shift-drag, combine with previous selection; otherwise replace
    if (globalIsShiftDrag) {
      // Combine previous selection with new drag selection
      const combined = [...globalPreviousSelection];
      newSelection.forEach((cell) => {
        if (!combined.some((c) => c.key === cell.key)) {
          combined.push(cell);
        }
      });
      setSelectedCells(combined);
    } else {
      setSelectedCells(newSelection);
    }
  };

  // Prevent default drag behavior
  const handleDragStart = (event) => {
    event.preventDefault();
  };

  const truncateText = (text, maxLength = 8) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Aktuelle Bearbeiten-Callbacks fuer den (nur bei onAdjustEntry-Wechsel neu
  // registrierten) mouseup-Listener, ohne veraltete Closures.
  const editHandlersRef = useRef({ onEditEntry, onDeleteEntry });
  editHandlersRef.current = { onEditEntry, onDeleteEntry };

  // Global mouseup listener
  useEffect(() => {
    const handleMouseUp = (event) => {
      if (globalDragMode) {
        const mode = globalDragMode;
        const entry = globalDragEntry;
        const moved = globalDragMoved;
        const preview = globalDragPreview;
        const startPos = globalDragStartPos;

        globalDragMode = null;
        globalDragEntry = null;
        globalDragDay = null;
        globalDragMoved = false;
        globalDragPreview = null;
        globalDragStartPos = null;
        setDragPreviewState(null);

        // Kein Drag, nur ein Klick auf den Zellkoerper: Bearbeitung oeffnen.
        // Resize-Griffe oeffnen bewusst nichts, ein Klick darauf bleibt folgenlos.
        if (!moved) {
          const isClick =
            mode === "move" &&
            entry &&
            startPos &&
            Math.hypot(event.clientX - startPos.x, event.clientY - startPos.y) <=
              CLICK_MAX_DISTANCE_PX;
          if (isClick) {
            const { onEditEntry: edit, onDeleteEntry: del } = editHandlersRef.current;
            if (edit) edit(entry);
            else if (del) del(entry);
          }
          return;
        }

        if (!preview || !entry || !onAdjustEntry) {
          return;
        }

        // endSlot ist bereits die exklusive Slot-Grenze (= Ende der letzten
        // Viertelstunde), entspricht also direkt der End-Uhrzeit.
        const { hour: startHour, half: startHalf } = DateUtils.getTimeFromSlot(
          preview.startSlot
        );
        const { hour: endHour, half: endHalf } = DateUtils.getTimeFromSlot(
          preview.endSlot
        );
        const startTimeStr = DateUtils.formatTime(startHour, startHalf);
        const endTimeStr = DateUtils.formatTime(endHour, endHalf);

        // Kein Update, wenn der Eintrag am Ende wieder an derselben Stelle liegt.
        if (
          entry.date === preview.day &&
          entry.startTime === startTimeStr &&
          entry.endTime === endTimeStr
        ) {
          return;
        }

        onAdjustEntry(entry, {
          date: preview.day,
          startTime: startTimeStr,
          endTime: endTimeStr,
        });
        return;
      }

      if (globalIsSelecting) {
        globalIsSelecting = false;
        globalSelectionStart = null;
        globalIsShiftDrag = false;
        globalPreviousSelection = [];
        setIsSelecting(false);
        setSelectionStart(null);
      }
    };

    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [onAdjustEntry]);

  // Contract Info Helpers
  const getContractInfo = (contractUuid, entry = null) => {
    // Local entries: use entry fields directly
    if (!contractUuid && entry?.source === "local") {
      return {
        reference: entry.code || entry.contractTitle || "Lokal",
        displayTitle: entry.contractTitle || "Lokales Projekt",
        fullReference: entry.code || "",
        customer: entry.contractTitle || "",
      };
    }

    const contract = contracts.find((c) => c.uuid === contractUuid);
    return (
      contract || {
        reference: "Unbekannt",
        displayTitle: "Unbekannter Auftrag",
        fullReference: "Unbekannt",
        customer: "Unbekannt",
      }
    );
  };

  const getContractColor = (contractUuid, entry = null) => {
    // Local entries: look up project color by code
    if (!contractUuid && entry?.source === "local" && entry?.code) {
      const projectCode = entry.code.substring(0, 3).toUpperCase();
      const project = projects.find((p) => p.code?.toUpperCase() === projectCode);
      if (project && project.color) {
        return { bg: project.color, text: "#ffffff" };
      }
    }

    if (!contractUuid) {
      return { bg: "#6b7280", text: "#ffffff" };
    }

    // Try to find the contract and get project color
    const contract = contracts.find((c) => c.uuid === contractUuid);
    if (contract && contract.reference) {
      // Extract project code from reference (first 3 characters)
      const projectCode = contract.reference.substring(0, 3).toUpperCase();
      const project = projects.find((p) => p.code?.toUpperCase() === projectCode);
      if (project && project.color) {
        return { bg: project.color, text: "#ffffff" };
      }
    }

    // Fallback to hash-based color if no project color found
    const fallbackColors = [
      { bg: "#3b82f6", text: "#ffffff" },
      { bg: "#ef4444", text: "#ffffff" },
      { bg: "#10b981", text: "#ffffff" },
      { bg: "#f59e0b", text: "#ffffff" },
      { bg: "#8b5cf6", text: "#ffffff" },
      { bg: "#06b6d4", text: "#ffffff" },
    ];

    const hash = contractUuid.split("").reduce((a, b) => {
      a = (a << 5) - a + b.charCodeAt(0);
      return a & a;
    }, 0);

    return fallbackColors[Math.abs(hash) % fallbackColors.length];
  };

  const getUniqueContracts = () => {
    const uniqueContracts = new Map();

    timeEntries.forEach((entry) => {
      if (entry.source === "local") {
        const localKey = `local_${entry.code}`;
        if (!uniqueContracts.has(localKey)) {
          uniqueContracts.set(localKey, {
            uuid: localKey,
            info: getContractInfo("", entry),
            colors: getContractColor("", entry),
          });
        }
      } else if (entry.contractUuid && !uniqueContracts.has(entry.contractUuid)) {
        uniqueContracts.set(entry.contractUuid, {
          uuid: entry.contractUuid,
          info: getContractInfo(entry.contractUuid),
          colors: getContractColor(entry.contractUuid),
        });
      }
    });

    return Array.from(uniqueContracts.values());
  };

  // Die Bearbeitung einer belegten Zelle oeffnet bereits handleMouseDown (ohne
  // onAdjustEntry) bzw. der mouseup-Handler (Klick ohne Drag). Der native click
  // wird hier nur abgefangen, damit das Modal nicht doppelt aufgeht.
  const handleTimeEntryClick = (timeEntry, event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div className="calendar-container">
      {/* Calendar Card */}
      <div className="card">
        <div className="calendar-header">
          <div className="calendar-navigation-top">
            <select
              className="month-select"
              value={currentWeek.getMonth()}
              onChange={(e) => {
                const newDate = new Date(currentWeek);
                newDate.setMonth(parseInt(e.target.value));
                setCurrentWeek(newDate);
                setSelectedCells([]);
              }}
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i} value={i}>
                  {new Date(2000, i).toLocaleDateString("de-DE", {
                    month: "long",
                  })}
                </option>
              ))}
            </select>

            <input
              type="date"
              className="date-picker"
              value={DateUtils.formatDate(currentWeek)}
              onChange={(e) => {
                setCurrentWeek(new Date(e.target.value));
                setSelectedCells([]);
              }}
            />

            <button
              type="button"
              className="nav-button today-button"
              onClick={() => {
                setCurrentWeek(new Date());
                setSelectedCells([]);
              }}
              title="Zur aktuellen Kalenderwoche springen"
            >
              Heute
            </button>
          </div>

          <button className="nav-button" onClick={() => navigateWeek(-1)}>
            <svg
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              width="16"
              height="16"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <h3 className="week-title">{DateUtils.formatWeekTitle(weekDays)}</h3>

          <button className="nav-button" onClick={() => navigateWeek(1)}>
            <svg
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              width="16"
              height="16"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
        <div className="calendar-content">
          {/* Loading Overlay */}
          {loading && (
            <div className="calendar-loading-overlay">
              <div className="loading-spinner"></div>
              <span className="loading-text">Lade Kalender...</span>
            </div>
          )}
          <div
            className={`calendar-scroll-container ${loading ? "loading" : ""}`}
            ref={scrollContainerRef}
          >
            <div
              className="calendar-grid-claude"
              ref={calendarRef}
              draggable="false"
              onDragStart={handleDragStart}
            >
              {/* Time Column */}
              <div className="time-column-claude">
                <div className="time-header">Zeit</div>
                {hours.map((hour) => (
                  <div key={`time-${hour}`} className="time-slot-group">
                    <div className="time-slot time-full">
                      {DateUtils.formatTime(hour, false)}
                    </div>
                    <div className="time-slot time-half">
                      {DateUtils.formatTime(hour, true)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Day Columns */}
              {weekDays.map((day, dayIndex) => {
                const dayKind = getDayKind(day);
                const fullDayEntries = getFullDayEntriesForDay(day);
                const feiertagName = getFeiertagName(day);
                return (
                <div
                  key={`day-${dayIndex}`}
                  className={`day-column-claude${dayKind ? ` day-${dayKind}` : ""}${fullDayEntries.length > 0 ? " day-absence" : ""}`}
                >
                  <div className="day-header">
                    <span className="day-header-label">
                      {DateUtils.formatDayHeader(day)}
                    </span>
                    <div className="day-header-badges">
                      {dayKind === "feiertag" && (
                        <span
                          className="absence-pill pill-feiertag"
                          title={feiertagName || "Feiertag"}
                        >
                          {feiertagName || "Feiertag"}
                        </span>
                      )}
                      {dayKind === "betriebsferien" && (
                        <span
                          className="absence-pill pill-betriebsferien"
                          title="Betriebsferien (vorgearbeitet)"
                        >
                          Betriebsferien
                        </span>
                      )}
                      {fullDayEntries.map((entry) => (
                        <button
                          key={`absence-${entry.uuid || entry.date}-${entry.typeName}`}
                          type="button"
                          className="absence-pill pill-absence"
                          title={`${entry.typeName || "Abwesenheit"} — Klicken zum Bearbeiten`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (onEditEntry) onEditEntry(entry);
                            else if (onDeleteEntry) onDeleteEntry(entry);
                          }}
                        >
                          {entry.typeName || "Abwesenheit"}
                        </button>
                      ))}
                    </div>
                  </div>
                  {hours.map((hour) => (
                    <div
                      key={`day-${dayIndex}-hour-${hour}`}
                      className="hour-group"
                    >
                      {/* Full Hour Cell */}
                      <div
                        className={getCellClassName(day, hour, false)}
                        onMouseDown={(e) =>
                          handleMouseDown(day, hour, false, e)
                        }
                        onMouseEnter={() => handleMouseEnter(day, hour, false)}
                        onContextMenu={(e) =>
                          handleCellContextMenu(day, hour, false, e)
                        }
                        onDragStart={handleDragStart}
                        title={(() => {
                          const entry = getTimeEntryForCell(day, hour, false);
                          if (entry) {
                            const contractInfo = getContractInfo(
                              entry.contractUuid, entry
                            );
                            const isArbeit =
                              entry.typeName?.toLowerCase() === "arbeit" ||
                              entry.typeName?.toLowerCase() === "work";

                            if (isArbeit) {
                              return `${DateUtils.formatDayHeader(
                                day
                              )} - ${DateUtils.formatTime(
                                hour,
                                false
                              )}\nAuftrag: ${
                                contractInfo.fullReference
                              }\nKunde: ${contractInfo.customer}\nZeit: ${
                                entry.startTime
                              }-${entry.endTime}\n${
                                entry.description || "Keine Beschreibung"
                              }`;
                            } else {
                              return `${DateUtils.formatDayHeader(
                                day
                              )} - ${DateUtils.formatTime(hour, false)}\nArt: ${
                                entry.typeName || "Unbekannte Aktivität"
                              }\nZeit: ${entry.startTime}-${entry.endTime}\n${
                                entry.description || "Keine Beschreibung"
                              }`;
                            }
                          }
                          return `${DateUtils.formatDayHeader(
                            day
                          )} - ${DateUtils.formatTime(hour, false)}`;
                        })()}
                        style={(() => {
                          const entry = getTimeEntryForCell(day, hour, false);
                          if (entry) {
                            const colors = getContractColor(entry.contractUuid, entry);
                            return {
                              '--occupied-color': colors.bg,
                              color: colors.text,
                            };
                          }
                          return {};
                        })()}
                        onClick={(e) => {
                          const entry = getTimeEntryForCell(day, hour, false);
                          if (entry) handleTimeEntryClick(entry, e);
                        }}
                      >
                        {(() => {
                          const entry = getTimeEntryForCell(day, hour, false);
                          if (!entry) return null;
                          const isFirst = isFirstCellOfEntry(day, hour, false, entry);
                          const isLast = isLastCellOfEntry(day, hour, false, entry);
                          let content = null;
                          if (isFirst) {
                            const contractInfo = getContractInfo(
                              entry.contractUuid, entry
                            );
                            const displayReference = (entry.contractUuid || entry.source === "local")
                              ? contractInfo.reference
                              : entry.typeName || "Aktivität";

                            content = (
                              <div className="cell-content">
                                <div className="contract-reference">
                                  {truncateText(displayReference, 10)}
                                </div>
                                <div className="contract-customer">
                                  {truncateText(contractInfo.customer, 8)}
                                </div>
                              </div>
                            );
                          }
                          return (
                            <>
                              {content}
                              {onAdjustEntry && !entry.isFullDay && isFirst && (
                                <div
                                  className="calendar-resize-handle calendar-resize-handle--top"
                                  title="Anfang ziehen"
                                  onMouseDown={(e) =>
                                    handleResizeMouseDown(entry, day, "start", e)
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                />
                              )}
                              {onAdjustEntry && !entry.isFullDay && isLast && (
                                <div
                                  className="calendar-resize-handle calendar-resize-handle--bottom"
                                  title="Ende ziehen"
                                  onMouseDown={(e) =>
                                    handleResizeMouseDown(entry, day, "end", e)
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                />
                              )}
                            </>
                          );
                        })()}
                      </div>

                      {/* Half Hour Cell */}
                      <div
                        className={getCellClassName(day, hour, true)}
                        onMouseDown={(e) => handleMouseDown(day, hour, true, e)}
                        onMouseEnter={() => handleMouseEnter(day, hour, true)}
                        onContextMenu={(e) =>
                          handleCellContextMenu(day, hour, true, e)
                        }
                        onDragStart={handleDragStart}
                        title={(() => {
                          const entry = getTimeEntryForCell(day, hour, true);
                          if (entry) {
                            const contractInfo = getContractInfo(
                              entry.contractUuid, entry
                            );
                            const isArbeit =
                              entry.typeName?.toLowerCase() === "arbeit" ||
                              entry.typeName?.toLowerCase() === "work";

                            if (isArbeit) {
                              return `${DateUtils.formatDayHeader(
                                day
                              )} - ${DateUtils.formatTime(
                                hour,
                                true
                              )}\nAuftrag: ${
                                contractInfo.fullReference
                              }\nKunde: ${truncateText(
                                contractInfo.customer,
                                10
                              )}\nZeit: ${entry.startTime}-${entry.endTime}\n${
                                entry.description || "Keine Beschreibung"
                              }`;
                            } else {
                              return `${DateUtils.formatDayHeader(
                                day
                              )} - ${DateUtils.formatTime(hour, true)}\nArt: ${
                                entry.typeName || "Unbekannte Aktivität"
                              }\nZeit: ${entry.startTime}-${entry.endTime}\n${
                                entry.description || "Keine Beschreibung"
                              }`;
                            }
                          }
                          return `${DateUtils.formatDayHeader(
                            day
                          )} - ${DateUtils.formatTime(hour, true)}`;
                        })()}
                        style={(() => {
                          const entry = getTimeEntryForCell(day, hour, true);
                          if (entry) {
                            const colors = getContractColor(entry.contractUuid, entry);
                            return {
                              '--occupied-color': colors.bg,
                              color: colors.text,
                            };
                          }
                          return {};
                        })()}
                        onClick={(e) => {
                          const entry = getTimeEntryForCell(day, hour, true);
                          if (entry) handleTimeEntryClick(entry, e);
                        }}
                      >
                        {(() => {
                          const entry = getTimeEntryForCell(day, hour, true);
                          if (!entry) return null;
                          const isFirst = isFirstCellOfEntry(day, hour, true, entry);
                          const isLast = isLastCellOfEntry(day, hour, true, entry);
                          let content = null;
                          if (isFirst) {
                            const contractInfo = getContractInfo(
                              entry.contractUuid, entry
                            );
                            const displayReference = (entry.contractUuid || entry.source === "local")
                              ? contractInfo.reference
                              : entry.typeName || "Aktivität";

                            content = (
                              <div className="cell-content">
                                <div className="contract-reference">
                                  {truncateText(displayReference, 8)}
                                </div>
                                <div className="contract-customer">
                                  {truncateText(contractInfo.customer, 10)}
                                </div>
                              </div>
                            );
                          }
                          return (
                            <>
                              {content}
                              {onAdjustEntry && !entry.isFullDay && isFirst && (
                                <div
                                  className="calendar-resize-handle calendar-resize-handle--top"
                                  title="Anfang ziehen"
                                  onMouseDown={(e) =>
                                    handleResizeMouseDown(entry, day, "start", e)
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                />
                              )}
                              {onAdjustEntry && !entry.isFullDay && isLast && (
                                <div
                                  className="calendar-resize-handle calendar-resize-handle--bottom"
                                  title="Ende ziehen"
                                  onMouseDown={(e) =>
                                    handleResizeMouseDown(entry, day, "end", e)
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                />
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
                );
              })}
            </div>{" "}
            {/* Schliesst calendar-grid-claude */}
          </div>{" "}
          {/* Schliesst calendar-content */}
          {/* Floating Action Button - ausserhalb calendar-content */}
          {selectedCells.length > 0 && onCreateEntry && !globalDragMode && (
            <div className="floating-action-button">
              <button className="fab-button" onClick={onCreateEntry}>
                <svg
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  width="24"
                  height="24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                <span className="fab-badge">{selectedCells.length}</span>
              </button>
            </div>
          )}
          {/* Rechtsklick-Kontextmenue: "Neuer Eintrag" + Schnellmodus */}
          {contextMenu && (
            <div
              className="calendar-context-menu"
              style={{ position: "fixed", top: contextMenu.y, left: contextMenu.x }}
              onClick={(e) => e.stopPropagation()}
            >
              {onCreateEntry && (
                <button
                  type="button"
                  className="calendar-context-menu__item"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setContextMenu(null);
                    onCreateEntry();
                  }}
                >
                  Neuer Eintrag…
                </button>
              )}
              {onQuickCreateEntry && recentProjects.length > 0 && (
                <>
                  <div className="calendar-context-menu__divider" />
                  <div className="calendar-context-menu__label">
                    Schnell buchen (ohne Buchungstext)
                  </div>
                  {recentProjects.map((project) => (
                    <button
                      key={project.code}
                      type="button"
                      className="calendar-context-menu__item"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setContextMenu(null);
                        onQuickCreateEntry(project);
                      }}
                    >
                      {project.code} - {project.name}
                    </button>
                  ))}
                </>
              )}
            </div>
          )}
        </div>{" "}
        {/* Schliesst card */}
        {/* Legend Card */}
        <div className="calendar-legend-claude">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Legende</h3>
            </div>
            <div className="legend-content">
              <div className="legend-section">
                <h4>Zeiterfassung:</h4>
                <div className="legend-items">
                  <div className="legend-item">
                    <div className="legend-color selected"></div>
                    <span>Ausgewählt</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color available"></div>
                    <span>Verfügbar</span>
                  </div>
                </div>
              </div>

              {getUniqueContracts().length > 0 && (
                <div className="legend-section">
                  <h4>Aufträge in dieser Woche:</h4>
                  <div className="legend-items contracts">
                    {getUniqueContracts().map((contract) => (
                      <div
                        key={contract.uuid}
                        className="legend-item contract-item"
                      >
                        <div
                          className="legend-color contract-color"
                          style={{
                            backgroundColor: contract.colors.bg,
                            color: contract.colors.text,
                          }}
                        >
                          {contract.info.reference}
                        </div>
                        <span title={contract.info.customer}>
                          {contract.info.displayTitle}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calendar;
